import React from "react";

import purchaseTerms from "@purchase-terms";
import { isSubscriptionOrderType } from "~/shared/utils/sharedUtils";

// purchaseTermsVariantFor picks which disclosure body to render.
//
// A one-time order gets a body WITHOUT the recurring-billing and cancellation terms,
// because telling that customer "this is a recurring subscription" would be false --
// and this text is quoted back to card issuers, where a claim they can disprove
// discredits everything submitted with it.
//
// Unknown order types fall back to one_time, matching KickOrderTypeIsSubscription,
// which also defaults to false. TestSubscriptionOrderTypesMatchCheckoutJS fails CI if
// the two lists drift, which is what stops a real subscription landing here.
export const purchaseTermsVariantFor = (orderType) => {
  const raw = isSubscriptionOrderType(orderType)
    ? purchaseTerms.variants.subscription
    : purchaseTerms.variants.one_time;
  return {
    version: raw.version,
    body: raw.body,
    checkboxLabel: raw.checkbox_label,
  };
};

/**
 * PurchaseTermsDisclosure renders the auto-renewal / refund terms immediately above
 * the pay button.
 *
 * Two things about this component are load-bearing and should not be "tidied":
 *
 * 1. The text comes from app/config/purchase_terms.json, the SAME file the Go server
 *    embeds. The customer's acceptance is stored with a SHA-256 hash of that body, so
 *    if this component ever renders its own copy of the wording the hash stops
 *    matching and every acceptance recorded afterwards becomes unverifiable — useless
 *    as chargeback evidence and, worse, a record that looks like proof but isn't.
 *
 * 2. Which variant renders depends on whether the order recurs. See
 *    purchaseTermsVariantFor above.
 *
 * 3. It renders at the payment step, before the charge. Placement is material: ROSCA
 *    and the state automatic-renewal laws ask for material terms to be disclosed
 *    clearly and conspicuously *before* billing information is obtained, and a card
 *    issuer weighing a dispute cares whether the customer saw this next to the pay
 *    button or several steps earlier. Do not move it behind a modal, an accordion, or
 *    a "read more" link.
 */
// EMPHASIS_PHRASES are decorated in the rendered output. The hashed body stays plain
// text and keeps this exact wording; only the markup is added at render time.
//
// That separation matters: the acceptance hash covers the WORDS the customer read, and
// linking or bolding a phrase does not change them. Putting markup in the JSON instead
// would put it into the hash, and every issuer-facing quote of the body would carry raw
// HTML.
const CARE_MESSAGE_PHRASE = "send us a Care Message";
const CARE_MESSAGE_PATH = "/me/messages/care";
const ONE_TIME_PHRASE =
  "This is a one-time order. You will only be charged once.";

const EMPHASIS_PHRASES = [
  {
    phrase: CARE_MESSAGE_PHRASE,
    render: (text, key) => (
      <a key={key} href={CARE_MESSAGE_PATH} style={{ color: "inherit" }}>
        {text}
      </a>
    ),
  },
  {
    // The single most consequential thing a one-time buyer can misread is whether they
    // just started a subscription. Bolding it is the cheapest way to make the answer
    // survive a skim.
    //
    // Phrased positively on purpose. "will not renew" hinges on a negation, and a
    // negation is exactly what a skimming reader drops -- turning it into the opposite
    // of what it says. "You will only be charged once" cannot invert that way.
    phrase: ONE_TIME_PHRASE,
    render: (text, key) => <strong key={key}>{text}</strong>,
  },
];

// decorateBody splits the body around each known phrase and decorates the matches.
//
// A phrase that is not present is skipped, so a copy edit degrades to plain text rather
// than breaking checkout. That failure is silent, which is why each phrase is pinned by
// a test rather than left to be noticed.
const decorateBody = (body) => {
  let segments = [body];

  EMPHASIS_PHRASES.forEach(({ phrase, render }, phraseIndex) => {
    segments = segments.flatMap((segment, segmentIndex) => {
      if (typeof segment !== "string") {
        return [segment];
      }
      const at = segment.indexOf(phrase);
      if (at === -1) {
        return [segment];
      }
      return [
        segment.slice(0, at),
        render(phrase, `emph-${phraseIndex}-${segmentIndex}`),
        segment.slice(at + phrase.length),
      ];
    });
  });

  return segments;
};

const PurchaseTermsDisclosure = ({ orderType, checked, onChange, error }) => {
  const variant = purchaseTermsVariantFor(orderType);
  return (
    <div data-terms-version={variant.version}>
      <div
        data-testid="purchase-terms-disclosure"
        style={{
          marginTop: "24px",
          padding: "16px",
          border: "1px solid #d8d8d8",
          borderRadius: "6px",
          backgroundColor: "#fafafa",
          fontSize: "13px",
          lineHeight: "1.5",
          color: "#333",
          whiteSpace: "pre-line",
        }}
      >
        {decorateBody(variant.body)}
      </div>

      {variant.checkboxLabel ? (
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            marginTop: "16px",
            fontSize: "13px",
            lineHeight: "1.5",
            color: "#333",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            data-testid="purchase-terms-accept"
            checked={Boolean(checked)}
            onChange={(event) => onChange(event.target.checked)}
            style={{ marginTop: "2px", flexShrink: 0 }}
          />
          <span>{variant.checkboxLabel}</span>
        </label>
      ) : null}

      {error ? (
        <p
          data-testid="purchase-terms-error"
          style={{ margin: "8px 0 0", fontSize: "13px", color: "#c0392b" }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default PurchaseTermsDisclosure;
